import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { X } from 'lucide-react';
import type { BookingBrandColors } from '../../utils/booking-brand-colors';
import { buildBookingModalFormStyles } from '../../utils/booking-modal-form-styles';
import type { ShopCatalogMembership } from '../../services/shop';
import { submitMembershipRequest } from '../../services/shop';
import { formatMoneyFromCents } from '../../utils/format-money';

type ShopMembershipRequestModalProps = {
  open: boolean;
  membership: ShopCatalogMembership | null;
  clubRouteKey: string | null;
  brandColors: BookingBrandColors;
  onClose: () => void;
  onSuccess: (membershipName: string) => void;
};

const ShopMembershipRequestModal: React.FC<ShopMembershipRequestModalProps> = ({
  open,
  membership,
  clubRouteKey,
  brandColors,
  onClose,
  onSuccess,
}) => {
  const styles = useMemo(() => buildBookingModalFormStyles(brandColors), [brandColors]);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setError(null);
    setBusy(false);
  }, [open, membership?.id]);

  const modalSubtitle = membership
    ? `${membership.name}${
        membership.priceCents != null ? ` — from ${formatMoneyFromCents(membership.priceCents)}` : ''
      }`
    : 'Submit your details and the club will follow up.';

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!membership || !clubRouteKey) return;
    if (!firstName.trim() || !lastName.trim() || !email.trim()) {
      setError('First name, last name, and email are required.');
      return;
    }

    setBusy(true);
    setError(null);
    try {
      await submitMembershipRequest({
        productId: membership.id,
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: email.trim(),
        phone: phone.trim() || undefined,
        message: message.trim() || undefined,
        clubRouteKey,
      });
      onSuccess(membership.name);
      setFirstName('');
      setLastName('');
      setEmail('');
      setPhone('');
      setMessage('');
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not submit request');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : onClose}
      maxWidth={false}
      fullWidth={false}
      scroll="paper"
      aria-labelledby="shop-membership-request-modal-title"
      BackdropProps={{ sx: styles.dialogBackdropSx }}
      PaperProps={{ sx: styles.dialogPaperSx }}
    >
      <DialogTitle component="div" sx={styles.dialogTitleShellSx}>
        <Box sx={{ px: styles.padX, pt: styles.padYTop, pb: 1 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              gap: 1.5,
            }}
          >
            <Box sx={{ minWidth: 0, pr: 0.5 }}>
              <Typography component="h2" id="shop-membership-request-modal-title" sx={styles.modalTitleSx}>
                Request membership
              </Typography>
              <Typography component="p" sx={styles.helperTextSx}>
                {modalSubtitle}
              </Typography>
            </Box>
            <IconButton
              aria-label="Close dialog"
              onClick={onClose}
              disabled={busy}
              size="small"
              sx={styles.closeIconButtonSx}
            >
              <X size={18} strokeWidth={2.25} aria-hidden />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent sx={styles.dialogContentSx}>
        {error ? (
          <Alert severity="error" sx={styles.errorAlertSx}>
            {error}
          </Alert>
        ) : null}

        <Stack
          component="form"
          spacing={styles.gapFields}
          onSubmit={(e) => void handleSubmit(e)}
        >
          <Typography component="p" sx={styles.helperTextSx}>
            No payment today — the club will follow up. One active request per membership at a time.
          </Typography>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={styles.gapFields}>
            <Box sx={{ flex: 1 }}>
              <Typography component="label" htmlFor="membership-first-name" sx={styles.fieldLabelSx}>
                First name
              </Typography>
              <TextField
                id="membership-first-name"
                hiddenLabel
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                size="medium"
                fullWidth
                required
                disabled={busy}
                autoComplete="given-name"
                sx={styles.inputSx}
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography component="label" htmlFor="membership-last-name" sx={styles.fieldLabelSx}>
                Last name
              </Typography>
              <TextField
                id="membership-last-name"
                hiddenLabel
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                size="medium"
                fullWidth
                required
                disabled={busy}
                autoComplete="family-name"
                sx={styles.inputSx}
              />
            </Box>
          </Stack>

          <Box>
            <Typography component="label" htmlFor="membership-email" sx={styles.fieldLabelSx}>
              Email address
            </Typography>
            <TextField
              id="membership-email"
              type="email"
              hiddenLabel
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              size="medium"
              fullWidth
              required
              disabled={busy}
              autoComplete="email"
              sx={styles.inputSx}
            />
          </Box>

          <Box>
            <Typography component="label" htmlFor="membership-phone" sx={styles.fieldLabelSx}>
              Phone number (optional)
            </Typography>
            <TextField
              id="membership-phone"
              type="tel"
              hiddenLabel
              placeholder="(555) 123-4567"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              size="medium"
              fullWidth
              disabled={busy}
              autoComplete="tel"
              sx={styles.inputSx}
            />
          </Box>

          <Box>
            <Typography component="label" htmlFor="membership-message" sx={styles.fieldLabelSx}>
              Message (optional)
            </Typography>
            <TextField
              id="membership-message"
              hiddenLabel
              placeholder="Tell the club what you are looking for"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              size="medium"
              fullWidth
              multiline
              minRows={3}
              disabled={busy}
              sx={styles.inputSx}
            />
          </Box>

          <Button
            type="submit"
            variant="contained"
            fullWidth
            disableElevation
            disabled={busy || !membership}
            sx={styles.primaryCtaSx}
          >
            {busy ? <CircularProgress size={20} color="inherit" /> : 'Submit request'}
          </Button>
        </Stack>
      </DialogContent>
    </Dialog>
  );
};

export default ShopMembershipRequestModal;
